// Public page-view tracker for swank12.com.
// No auth. Uses fetch (not axios). Fire-and-forget; never throws; never blocks navigation.

const ENDPOINT = "https://figazuresql.swancreate.com/api/crm/sk12_siteactivity";

// Column max lengths (see backend contract). Used to truncate string fields.
const MAX = {
  SessionId: 64,
  VisitorId: 64,
  Path: 200,
  PageTitle: 200,
  Referrer: 200,
  DeviceType: 20,
  Browser: 50,
  OS: 50,
  ScreenSize: 20,
  Language: 20,
  State: 50,
  Country: 50,
  City: 100,
  UserAgent: 300,
};

function truncate(value, max) {
  if (value === null || value === undefined) return value;
  const s = String(value);
  return s.length > max ? s.slice(0, max) : s;
}

// A random-ish id, preferring crypto. Falls back without relying on Math.random for the primary path.
function randomId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      const buf = new Uint8Array(16);
      window.crypto.getRandomValues(buf);
      let out = "";
      for (let i = 0; i < buf.length; i++) {
        out += buf[i].toString(16).padStart(2, "0");
      }
      return out;
    }
  } catch (e) {
    // fall through to timestamp-based fallback
  }
  // Last-resort fallback: timestamp + high-resolution perf clock.
  const perf =
    typeof performance !== "undefined" && performance.now
      ? Math.floor(performance.now() * 1000)
      : 0;
  return "id-" + new Date().getTime().toString(36) + "-" + perf.toString(36);
}

// Persistent per-visitor id (survives across sessions).
function getVisitorId() {
  const KEY = "sk12_visitor_id";
  try {
    let id = window.localStorage.getItem(KEY);
    if (!id) {
      id = randomId();
      window.localStorage.setItem(KEY, id);
    }
    return id;
  } catch (e) {
    return randomId();
  }
}

// Per-session id (cleared when the tab/session ends).
function getSessionId() {
  const KEY = "sk12_session_id";
  try {
    let id = window.sessionStorage.getItem(KEY);
    if (!id) {
      id = randomId();
      window.sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch (e) {
    return randomId();
  }
}

function detectDeviceType() {
  const ua = navigator.userAgent || "";
  if (/Mobi|iPhone|iPod|Android.*Mobile/.test(ua)) return "Mobile";
  if (/iPad|Tablet|Android/.test(ua)) return "Tablet";
  return "Desktop";
}

function detectBrowser() {
  const ua = navigator.userAgent || "";
  if (/Edg/.test(ua)) return "Edge";
  if (/OPR/.test(ua)) return "Opera";
  if (/Firefox/.test(ua)) return "Firefox";
  if (/Chrome/.test(ua)) return "Chrome";
  if (/Safari/.test(ua)) return "Safari";
  return "Other";
}

function detectOS() {
  const ua = navigator.userAgent || "";
  if (/Windows/.test(ua)) return "Windows";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Mac OS X|Macintosh/.test(ua)) return "macOS";
  if (/Android/.test(ua)) return "Android";
  if (/Linux/.test(ua)) return "Linux";
  return "Other";
}

function screenSize() {
  try {
    return window.screen.width + "x" + window.screen.height;
  } catch (e) {
    return "";
  }
}

// Resolve coarse geo from IP. Cached in sessionStorage. Never throws.
async function getGeo() {
  const KEY = "sk12_geo";
  try {
    const cached = window.sessionStorage.getItem(KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        // ignore bad cache
      }
    }
  } catch (e) {
    // sessionStorage unavailable
  }

  const fetchJson = async (url, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  let geo = {};
  try {
    const primary = await fetchJson("https://ipapi.co/json/", 2500);
    if (primary && (primary.region || primary.country_name || primary.city)) {
      geo = {
        State: primary.region || "",
        Country: primary.country_name || "",
        City: primary.city || "",
      };
    } else {
      const backup = await fetchJson("https://ipwho.is/", 2500);
      if (backup && (backup.region || backup.country || backup.city)) {
        geo = {
          State: backup.region || "",
          Country: backup.country || "",
          City: backup.city || "",
        };
      }
    }
  } catch (e) {
    geo = {};
  }

  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(geo));
  } catch (e) {
    // ignore
  }
  return geo;
}

// Dedupe guard: skip identical path fired within 800ms (perf clock, not wall clock).
let lastKey = null;
let lastTime = 0;

export async function trackPageView(path, title) {
  try {
    if (!path || path.startsWith("/admin")) return;

    const now =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : 0;
    if (path === lastKey && now - lastTime < 800) return;
    lastKey = path;
    lastTime = now;

    const geo = await getGeo();

    const payload = {
      SessionId: truncate(getSessionId(), MAX.SessionId),
      VisitorId: truncate(getVisitorId(), MAX.VisitorId),
      Path: truncate(path, MAX.Path),
      PageTitle: truncate(title, MAX.PageTitle),
      Referrer: truncate(document.referrer, MAX.Referrer),
      DeviceType: truncate(detectDeviceType(), MAX.DeviceType),
      Browser: truncate(detectBrowser(), MAX.Browser),
      OS: truncate(detectOS(), MAX.OS),
      ScreenSize: truncate(screenSize(), MAX.ScreenSize),
      Language: truncate(navigator.language, MAX.Language),
      State: truncate(geo.State, MAX.State),
      Country: truncate(geo.Country, MAX.Country),
      City: truncate(geo.City, MAX.City),
      UserAgent: truncate(navigator.userAgent, MAX.UserAgent),
    };

    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch (e) {
    // Never throw; never block navigation.
  }
}
